// @ts-check
// @ts-ignore

/* eslint-disable @typescript-eslint/no-var-requires */
import { OverlayProvider } from '@react-aria/overlays';
import { SSRProvider } from '@react-aria/ssr';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AutoTrackingProvider } from 'auto-tracking';
import { ErrorBoundary } from 'common-hooks';
import { RumProvider, useRum } from 'common-rum';
import { useInitializeStorage } from 'common-storage';
import { getEnvironment, getEnvironmentStats } from 'common-utils';
import {
  addCssReset,
  GetUserConsentDialog,
  ToasterProvider,
} from 'design-system';
import { appWithTranslation } from 'next-i18next';
import { AppProps } from 'next/dist/shared/lib/router/router';
import Head from 'next/head';
import { ErrorInfo } from 'react';

import '../../public/assets/css/custom.css';
import { PreflightAuthContextConfiguration } from '../auth/preflight-auth-context-configuration';
import { WebErrorScreen } from '../components/generic-error-screen';
import { TrackingContextProvider } from '../contexts/tracking-context';
import { addGlobalCss } from '../global-css';
import { PageView } from '../utils/page-view';

// @ts-check
// @ts-ignore

/* eslint-disable @typescript-eslint/no-var-requires */
const nextI18NextConfig = require('../../next-i18next.config.js');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 30_000,
    },
  },
});

declare global {
  interface Window {
    __mocksReady: Promise<ServiceWorkerRegistration | undefined>;
  }
}

if (process.env.NEXT_PUBLIC_ENABLE_MOCKS === 'true') {
  const mocksReadyPromise = import('../mocks').then(({ initializeMocks }) =>
    initializeMocks(),
  );
  if (typeof window !== 'undefined') {
    // this allows any part of the code to wait for mocks to be ready before making any requests
    window['__mocksReady'] = mocksReadyPromise;
  }
}

const App = ({ Component, pageProps }: AppProps) => {
  addCssReset();
  addGlobalCss();
  useInitializeStorage();
  const rum = useRum();
  const logErrorToGrafana = ({
    error,
    errorInfo,
  }: {
    error: Error;
    errorInfo: ErrorInfo;
  }) => {
    try {
      rum.pushError(error, {
        type: 'Generic Error Screen',
        context: {
          errorName: 'App error',
          errorInfo: `${errorInfo}`,
        },
      });
    } catch {}
  };
  return (
    <>
      <Head>
        <title>Paperpal</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>
      <RumProvider
        grafana={{
          host: process.env.NEXT_PUBLIC_GRAFANA_FARO_HOST,
          key: process.env.NEXT_PUBLIC_GRAFANA_FARO_KEY,
        }}
        app={process.env.NEXT_PUBLIC_PRODUCT}
        env={process.env.NEXT_PUBLIC_ENV}
        version={process.env.NEXT_PUBLIC_VERSION}
      >
        <ErrorBoundary
          fallbackProps={{ stats: getEnvironmentStats() }}
          fallback={WebErrorScreen}
          onError={(error) => logErrorToGrafana(error)}
          email={
            process.env.NEXT_PUBLIC_CHINA === 'true'
              ? 'paperpal@editage.cn'
              : 'hello@paperpal.com'
          }
        >
          <AutoTrackingProvider
            showCookieBanner
            productDetails={{ name: 'paperpal-preflight', channel: 'web' }}
            renderUserConsentDialog={(renderProps) => (
              <GetUserConsentDialog {...renderProps} />
            )}
          >
            <TrackingContextProvider pageProps={pageProps}>
              <QueryClientProvider client={queryClient}>
                <ToasterProvider>
                  <SSRProvider>
                    <OverlayProvider>
                      <PreflightAuthContextConfiguration
                        domain={
                          getEnvironment(process.env.NEXT_PUBLIC_ENV) === 'PROD'
                            ? '.paperpal.com'
                            : 'localhost'
                        }
                      >
                        <PageView pageProps={pageProps} />
                        <Component {...pageProps} />
                      </PreflightAuthContextConfiguration>
                    </OverlayProvider>
                  </SSRProvider>
                </ToasterProvider>
              </QueryClientProvider>
            </TrackingContextProvider>
          </AutoTrackingProvider>
        </ErrorBoundary>
      </RumProvider>
    </>
  );
};

export default appWithTranslation(App, nextI18NextConfig);
